import { DEFAULT_MAX_ATTACHMENT_BYTES, MAX_FILES, MAX_INTRO_CHARS, MAX_PLAINTEXT_BYTES, MAX_PRIVATE_PLAINTEXT_BYTES, MIN_PIN_DIGITS } from "@timecapsule/crypto";
import type { ReactNode } from "react";
import { Footer, PROBLEM_ADDRESS, PublicHeader } from "../App";
import { Doc } from "../Doc";
import { LOCALES, useLang } from "../i18n";
import { GuideEn } from "./guide/en";
import { GuideSr } from "./guide/sr";
import { GuideEs } from "./guide/es";
import { GuideFr } from "./guide/fr";
import { GuidePt } from "./guide/pt";
import { GuideRu } from "./guide/ru";
import { GuideHi } from "./guide/hi";
import { GuideId } from "./guide/id";
import { GuideBn } from "./guide/bn";
import { GuideAr } from "./guide/ar";
import { GuideZh } from "./guide/zh";
import { GuideUr } from "./guide/ur";
import { GuideJa } from "./guide/ja";

/** Whole units for the guide: "16 KiB", "10 MiB". */
const size = (n: number) => (n >= 1024 * 1024 ? `${n / 1024 / 1024} MiB` : `${n / 1024} KiB`);

/**
 * Pilot limits as the guide states them. The crypto ones are imported; the server's are copied
 * here and must follow apps/api: MAX_SUBMISSIONS_PER_DAY (validate.ts), MAX_UPLOAD_BYTES_PER_DAY
 * and MAX_UNLINKED_BLOBS (blobs.ts), MAX_PIN_ATTEMPTS (keys.ts), SESSION_TTL_SECONDS (session.ts),
 * the plan numbers of apps/api/src/plans.ts (Free 5 stamps, 25 MiB, 256 KiB; Legacy 100; Start 50, 1 GiB, 5 MiB).
 */
const LIMITS = {
  publicText: size(MAX_PLAINTEXT_BYTES),
  introChars: MAX_INTRO_CHARS,
  privateText: size(MAX_PRIVATE_PLAINTEXT_BYTES),
  files: MAX_FILES,
  fileBytes: size(DEFAULT_MAX_ATTACHMENT_BYTES),
  planFileBytes: size(5 * 1024 * 1024),
  freeStamps: 5,
  planStamps: 50,
  legacyStamps: 100,
  freeStorage: size(25 * 1024 * 1024),
  planStorage: "1 GiB",
  pinDigits: MIN_PIN_DIGITS,
  pinAttempts: 10,
  daily: 100,
  uploadDaily: size(50 * 1024 * 1024),
  unusedFiles: 20,
  sessionDays: 7,
};

export type GuideProps = { limits: typeof LIMITS; link: ReactNode };

/** One guide per language (2026-10-11: all thirteen; the added ones not yet reviewed by native speakers). */
const GUIDES = { en: GuideEn, sr: GuideSr, es: GuideEs, fr: GuideFr, pt: GuidePt, ru: GuideRu, hi: GuideHi, id: GuideId, bn: GuideBn, ar: GuideAr, zh: GuideZh, ur: GuideUr, ja: GuideJa };

/**
 * The member guide (4b step 4): one page in the member's language, open without logging in, so an
 * invite can point to it. Linked from the login page and every footer.
 */
export function Guide() {
  const { lang } = useLang();
  const Content = GUIDES[lang];
  return (
    <>
      <PublicHeader />
      <Doc>
        <div lang={LOCALES[lang].htmlLang} dir={LOCALES[lang].dir}><Content limits={LIMITS} link={<a href={`mailto:${PROBLEM_ADDRESS}`}>{PROBLEM_ADDRESS}</a>} /></div>
      </Doc>
      <Footer />
    </>
  );
}
